import React, { useState, useMemo, useEffect, useRef } from "react";
import { message, Modal, Button } from "antd";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { translations } from "../../utils/Translation/staticTranslation";
import {
  isProfileModuleDeleteBlocked,
  isProfileModuleReadOnly,
} from "../../utils/modulePermissionSidebar";
import ContactsTableWithFilters from "../../components/Contacts/ContactsTableWithFilters";
import ContactDetailView from "../../components/Contacts/ContactDetailView";
import CreateContactListModal from "../../components/Contacts/CreateContactListModal";
import { Contacts as ContactsApi } from "../../Apis/contactsApi";
import "../../components/Contacts/Contacts.css";

const Contacts = () => {
  const lang = localStorage.getItem("language") || "ar";
  const t = translations[lang]?.contactsPage || {};
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isFetchingRef = useRef(false);
  const user = useSelector((state) => state.user.user);
  const contactsReadOnly = isProfileModuleReadOnly(user, "contacts");
  const contactsCanDelete = !isProfileModuleDeleteBlocked(user, "contacts");
  
  // Get contact list ID from URL
  const contactListIdFromUrl = searchParams.get("view");

  const [loading, setLoading] = useState(false);
  const [contactsData, setContactsData] = useState([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [selectedContactList, setSelectedContactList] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingContactList, setEditingContactList] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contactListToDelete, setContactListToDelete] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    page: 1,
    pageSize: 10,
  });
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 600); // 600ms delay

    return () => clearTimeout(timer);
  }, [filters.search]);

  // Fetch contact lists function - reusable
  const fetchContactLists = async () => {
    // Prevent concurrent API calls
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    
    try {
      setLoading(true);
      const response = await ContactsApi.getContactLists({
        search: debouncedSearch || "",
        page: filters.page,
        pageSize: filters.pageSize,
      });

      if (response?.data?.results) {
        const mappedData = response.data.results.map((item) => ({
          id: item.id,
          name: item.name || "",
          description: item.description || "",
          contactsCount: item.total_contact_users || item.contacts_count || 0,
          excel_file_path: item.excel_file_path || null,
        }));

        setContactsData(mappedData);
        setContactsTotal(response.data.count || 0);
      }
    } catch (error) {
      console.error("Error fetching contact lists:", error);
      const currentT = translations[lang]?.contactsPage || {};
      message.error(
        currentT?.error_fetching_contacts || "Error fetching contact lists"
      );
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  // Handle URL-based navigation - sync selectedContactList with URL
  useEffect(() => {
    if (contactListIdFromUrl) {
      // If URL has view parameter, find and set the contact list
      const contactListId = parseInt(contactListIdFromUrl, 10);
      if (contactListId) {
        // Check if we need to update (different ID or not set)
        const needsUpdate = !selectedContactList || selectedContactList.id !== contactListId;
        
        if (needsUpdate) {
          // Find the contact list from current data or fetch it
          const foundList = contactsData.find((item) => item.id === contactListId);
          if (foundList) {
            setSelectedContactList(foundList);
          } else {
            // If not in current data, fetch it
            const fetchContactListById = async () => {
              try {
                const response = await ContactsApi.getContactLists({
                  page: 1,
                  pageSize: 1000,
                });
                if (response?.data?.results) {
                  const found = response.data.results.find(
                    (item) => item.id === contactListId
                  );
                  if (found) {
                    setSelectedContactList({
                      id: found.id,
                      name: found.name || "",
                      description: found.description || "",
                      contactsCount: found.total_contact_users || found.contacts_count || 0,
                      excel_file_path: found.excel_file_path || null,
                    });
                  }
                }
              } catch (error) {
                console.error("Error fetching contact list:", error);
              }
            };
            fetchContactListById();
          }
        }
      }
    } else {
      // If no view parameter, clear selected contact list
      if (selectedContactList) {
        setSelectedContactList(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactListIdFromUrl, location.pathname]);

  // Fetch contact lists from API
  useEffect(() => {
    fetchContactLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, filters.page, filters.pageSize, lang]);

  // Track previous selectedContactList to detect when navigating back
  const prevSelectedContactListRef = useRef(null);
  
  // Refresh data when navigating back from detail view
  useEffect(() => {
    // Only refresh if we had a selected contact list before and now it's null (navigating back)
    if (prevSelectedContactListRef.current !== null && selectedContactList === null) {
      // Reset the fetching ref to allow immediate refresh
      isFetchingRef.current = false;
      // Refresh the contact lists to show latest data
      fetchContactLists();
    }
    // Update the ref for next comparison
    prevSelectedContactListRef.current = selectedContactList;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContactList]);

  const pagination = useMemo(
    () => ({
      current: filters.page,
      pageSize: filters.pageSize,
      total: contactsTotal,
    }),
    [filters.page, filters.pageSize, contactsTotal]
  );

  const handleSearchChange = (value) => {
    setFilters((prev) => ({
      ...prev,
      search: value,
      page: 1,
    }));
  };

  const handlePageChange = (page) => {
    setFilters((prev) => ({
      ...prev,
      page: page,
    }));
  };

  const handleView = async (record) => {
    if (!record?.id) {
      message.error(t?.contact_list_id_missing || "Contact list ID is missing");
      return;
    }

    try {
      setDetailLoading(true);
      // Use the record data we already have (includes excel_file_path from the list)
      const mappedContactList = {
        id: record.id,
        name: record.name || "",
        description: record.description || "",
        contactsCount: record.contactsCount || 0,
        excel_file_path: record.excel_file_path || null,
      };

      setSelectedContactList(mappedContactList);
      
      // Update URL to include view parameter for proper browser back button support
      setSearchParams({ view: record.id.toString() });
    } catch (error) {
      console.error("Error setting contact list:", error);
      message.error(
        t?.error_fetching_contact_list || "Error fetching contact list details"
      );
    } finally {
      setDetailLoading(false);
    }
  };

  const handleBackToList = () => {
    // Reset the fetching ref to allow immediate refresh
    isFetchingRef.current = false;
    // Clear selected contact list
    setSelectedContactList(null);
    // Remove view parameter from URL to go back to main list
    setSearchParams({});
    // Immediately refresh the contact lists to show latest data
    fetchContactLists();
  };

  const handleEdit = (record) => {
    if (contactsReadOnly) return
    if (!record?.id) {
      message.error(t?.contact_list_id_missing || "Contact list ID is missing");
      return;
    }

    // Use the record data directly (already available from the table)
    const editData = {
      id: record.id,
      name: record.name || "",
      description: record.description || "",
    };

    setEditingContactList(editData);
    setShowCreateModal(true);
  };

  const handleDelete = (record) => {
    if (!contactsCanDelete) return
    if (!record?.id) {
      message.error(t?.contact_list_id_missing || "Contact list ID is missing");
      return;
    }

    setContactListToDelete(record);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!contactListToDelete?.id) {
      message.error(t?.contact_list_id_missing || "Contact list ID is missing");
      setShowDeleteModal(false);
      setContactListToDelete(null);
      return;
    }

    try {
      setLoading(true);
      const response = await ContactsApi.deleteContactList(contactListToDelete.id);

      if (
        response?.status === 200 ||
        response?.status === 204 ||
        response?.data
      ) {
        message.success(
          t?.contact_list_deleted_successfully || "Contact list deleted successfully"
        );
        
        // Get current pagination state before making the API call
        const currentPage = filters.page;
        const currentPageSize = filters.pageSize;
        const currentSearch = filters.search || "";
        
        // Reset the fetching ref to allow immediate refresh
        isFetchingRef.current = false;
        
        // ALWAYS call GET API immediately after successful deletion to show latest data
        await fetchContactLists();
      }
    } catch (error) {
      console.error("Error deleting contact list:", error);
      message.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          t?.error_deleting_contact_list ||
          "Error deleting contact list"
      );
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setContactListToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setContactListToDelete(null);
  };

  const handleCreateContactList = () => {
    if (contactsReadOnly) return
    setEditingContactList(null);
    setShowCreateModal(true);
  };

  const mapContactListData = (item) => ({
    id: item.id,
    name: item.name || "",
    description: item.description || "",
    contactsCount: item.total_contact_users || item.contacts_count || 0,
    excel_file_path: item.excel_file_path || null,
  });

  const navigateToContactListView = (contactList) => {
    setSelectedContactList(contactList);
    setSearchParams({ view: contactList.id.toString() });
    setLoading(false);
  };

  const handleCreateContactListSubmit = async (formData) => {
    try {
      setLoading(true);

      const payload = {
        name: formData.name,
        description: formData.description || "",
      };

      const isEditing = editingContactList && editingContactList.id;
      const response = isEditing
        ? await ContactsApi.updateContactList(editingContactList.id, payload)
        : await ContactsApi.createContactList(payload);

      if (
        response?.data?.success ||
        response?.status === 200 ||
        response?.status === 201
      ) {
        message.success(
          isEditing
            ? t?.contact_list_updated_successfully ||
                "Contact list updated successfully"
            : t?.contact_list_created_successfully ||
                "Contact list created successfully"
        );
        setShowCreateModal(false);
        setEditingContactList(null);
        isFetchingRef.current = false;

        if (!isEditing) {
          const responseData = response?.data || response;
          let contactListId = responseData?.id || responseData?.data?.id;
          let contactListData = null;

          if (responseData?.id && responseData?.name) {
            contactListData = responseData;
          } else if (responseData?.data?.id && responseData?.data?.name) {
            contactListData = responseData.data;
          }

          if (contactListData && contactListId) {
            navigateToContactListView(mapContactListData(contactListData));
            return;
          }

          try {
            const fetchResponse = await ContactsApi.getContactLists({
              page: 1,
              pageSize: 1000,
            });

            if (fetchResponse?.data?.results?.length > 0) {
              const found = contactListId
                ? fetchResponse.data.results.find(
                    (item) => item.id === contactListId
                  ) || fetchResponse.data.results[0]
                : fetchResponse.data.results[0];

              if (found) {
                navigateToContactListView(mapContactListData(found));
                return;
              }
            }
          } catch (fetchError) {
            console.error("Error fetching created contact list:", fetchError);
          }

          setFilters((prev) => ({ ...prev, page: 1 }));
        }

        await fetchContactLists();
      } else if (response?.data?.success === false) {
        const errorDetails = response.data.details || response.data.message;
        message.error(errorDetails || t?.error_creating_contact_list || "Error creating contact list");
      }
    } catch (error) {
      console.error("Error creating contact list:", error);

      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Extract field-specific errors (e.g., {"name": ["error message"]})
        let errorMessage = null;
        
        // Check for field-specific errors
        if (errorData.name && Array.isArray(errorData.name) && errorData.name.length > 0) {
          errorMessage = errorData.name[0];
        } else if (errorData.description && Array.isArray(errorData.description) && errorData.description.length > 0) {
          errorMessage = errorData.description[0];
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.details && typeof errorData.details === "string") {
          errorMessage = errorData.details;
        }
        
        // Fallback to default message
        if (!errorMessage) {
          errorMessage = t?.error_creating_contact_list || "Error creating contact list";
        }
        
        message.error(errorMessage);
      } else {
        message.error(t?.error_creating_contact_list || "Error creating contact list");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handler to update contact list and refresh main list
  const handleContactListUpdate = (updatedContactList) => {
    // Update the selected contact list
    setSelectedContactList(updatedContactList);
    
    // Also refresh the main contacts list to update the count
    // Reset the fetching ref to allow immediate refresh
    isFetchingRef.current = false;
    fetchContactLists();
  };

  // If a contact list is selected, show the detail view
  if (selectedContactList) {
    return (
      <ContactDetailView
        contactList={selectedContactList}
        onBack={handleBackToList}
        loading={detailLoading}
        onContactListUpdate={handleContactListUpdate}
        readOnly={contactsReadOnly}
        canDelete={contactsCanDelete}
      />
    );
  }

  return (
    <div className="contacts-page-container">
      {/* Page Title */}
      <h1 className="contacts-page-title">{t?.contacts || "Contacts"}</h1>

      {/* Table with Filters */}
      <ContactsTableWithFilters
        data={contactsData}
        loading={loading}
        searchValue={filters.search}
        pagination={pagination}
        onSearchChange={handleSearchChange}
        onPageChange={handlePageChange}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onCreateContactList={handleCreateContactList}
        readOnly={contactsReadOnly}
        canDelete={contactsCanDelete}
      />

      {/* Create/Edit Contact List Modal */}
      <CreateContactListModal
        visible={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditingContactList(null);
        }}
        onSubmit={handleCreateContactListSubmit}
        editData={editingContactList}
        loading={loading}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        open={showDeleteModal}
        onCancel={handleCancelDelete}
        footer={null}
        width={500}
        centered
        className="confirm-contact-modal"
        closeIcon={null}
      >
        <div className="confirm-contact-content">
          <div className="confirm-contact-icon">
            <div
              style={{
                width: "64px",
                height: "64px",
                borderRadius: "50%",
                backgroundColor: "#FEF3F2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="#F04438"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>
          <h2 className="confirm-contact-title">
            {t?.modal?.confirm_delete_contact_list || "Confirm Delete Contact Group"}
          </h2>
          <p className="confirm-contact-message">
            {t?.modal?.confirm_delete_contact_list_message ||
              "Are you sure you want to delete this contact group? This action cannot be undone."}
          </p>
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              gap: "12px",
              marginTop: "32px",
            }}
          >
            <Button
              onClick={handleCancelDelete}
              className="create-contact-modal-button"
              style={{
                minWidth: "120px",
                height: "46px",
                borderRadius: "50px",
                border: "1px solid #D0D5DD",
                backgroundColor: "#FFFFFF",
                color: "#344054",
              }}
            >
              {t?.modal?.cancel || "Cancel"}
            </Button>
            <Button
              onClick={handleConfirmDelete}
              type="primary"
              className="create-contact-modal-button-primary"
              style={{
                minWidth: "120px",
                height: "46px",
                borderRadius: "50px",
              }}
            >
              {t?.modal?.delete || "Delete"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Contacts;
